import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuthStore } from '@/store/auth';
import {
  markMaestroPanelClosed,
  markMaestroPanelOpen,
  shouldMaestroPanelStartOpen,
} from '@/components/ai-assistant/maestroSession';

export interface PageDataContext {
  // Analytics & Statistics
  statistics?: {
    transactionsPerHour?: number;
    errorRate?: number;
    averageLatency?: number;
    totalTransactions?: number;
    connectorName?: string;
  };
  // Transaction Data
  transactions?: Array<{
    transactionId: string;
    status: string;
    startDate: string;
    endDate: string;
    latency?: number;
    errorMessage?: string;
    payload?: any;
  }>;
  // Integration Configuration
  integrationConfig?: {
    source?: {
      name?: string;
      url?: string;
      type?: string;
      method?: string;
    };
    destination?: {
      name?: string;
      url?: string;
      type?: string;
      method?: string;
    };
    status?: string;
    lastRunDate?: string;
  };
  // Chart/Analytics Data
  analyticsData?: any;
}

interface MaestroContextType {
  pageData: PageDataContext | null;
  setPageData: (data: PageDataContext) => void;
  clearPageData: () => void;
  isPanelOpen: boolean;
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
}

const MaestroContext = createContext<MaestroContextType | undefined>(undefined);

export const MaestroContextProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const loggedIn = useAuthStore((state) => state.loggedIn);
  const [pageData, setPageDataState] = useState<PageDataContext | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const setPageData = (data: PageDataContext) => {
    setPageDataState(data);
  };

  const clearPageData = () => {
    setPageDataState(null);
  };

  const openPanel = useCallback(() => {
    setIsPanelOpen(true);
    markMaestroPanelOpen();
  }, []);

  const closePanel = useCallback(() => {
    setIsPanelOpen(false);
    markMaestroPanelClosed();
  }, []);

  const togglePanel = useCallback(() => {
    setIsPanelOpen((prev) => {
      const next = !prev;
      if (next) markMaestroPanelOpen();
      else markMaestroPanelClosed();
      return next;
    });
  }, []);

  useEffect(() => {
    if (!loggedIn) {
      setIsPanelOpen(false);
      return;
    }
    if (shouldMaestroPanelStartOpen()) {
      setIsPanelOpen(true);
      markMaestroPanelOpen();
    }
  }, [loggedIn]);

  return (
    <MaestroContext.Provider
      value={{
        pageData,
        setPageData,
        clearPageData,
        isPanelOpen,
        openPanel,
        closePanel,
        togglePanel,
      }}
    >
      {children}
    </MaestroContext.Provider>
  );
};

export const useMaestroContext = () => {
  const context = useContext(MaestroContext);
  if (!context) {
    throw new Error('useMaestroContext must be used within a MaestroContextProvider');
  }
  return context;
};


